import { useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

interface EngagementModel {
  id: string;
  name: string;
  subtitle: string;
  icon: string;
  description: string;
  bestFor: string[];
  notFor: string[];
  timeline: string;
  pricing: string;
  control: string;
  management: string;
  scalability: string;
  commitment: string;
  ip: string;
  pros: { icon: string; text: string }[];
  cons: { icon: string; text: string }[];
}

const models: EngagementModel[] = [
  {
    id: 'staff-aug',
    name: 'Staff Augmentation',
    subtitle: 'Engineers join your team',
    icon: 'ri-user-add-line',
    description:
      'Individual developers, DevOps engineers, or QA specialists who report directly to you and work within your existing workflows, tools, and sprints. You manage the day-to-day; we handle hiring, payroll, and compliance.',
    bestFor: [
      'Filling skill gaps fast',
      'Seasonal capacity spikes',
      'Backfilling departing employees',
      'Specific expertise (e.g., Kubernetes, ML)',
    ],
    notFor: [
      'End-to-end product delivery',
      'Complex multi-squad projects',
      'Zero internal management bandwidth',
    ],
    timeline: '48 hours – 2 weeks',
    pricing: 'Monthly per engineer',
    control: 'Full — you direct daily work',
    management: 'You manage tasks & sprints',
    scalability: 'Add/remove 1 engineer at a time',
    commitment: 'Monthly, 30-day notice',
    ip: 'Owned by you from day one',
    pros: [
      { icon: 'ri-flashlight-line', text: 'Fastest time-to-start — engineers can join within days' },
      { icon: 'ri-equalizer-line', text: 'Full control over architecture and tech decisions' },
      { icon: 'ri-exchange-dollar-line', text: 'No long-term contracts — scale up or down monthly' },
    ],
    cons: [
      { icon: 'ri-user-settings-line', text: 'Requires internal management and onboarding effort' },
      { icon: 'ri-team-line', text: 'Not ideal if you need an entire self-sufficient squad' },
    ],
  },
  {
    id: 'dedicated',
    name: 'Dedicated Team',
    subtitle: 'Fully managed remote squad',
    icon: 'ri-team-fill',
    description:
      'A complete cross-functional team — tech lead, engineers, QA, and DevOps — assembled and managed by us, but aligned with your roadmap. We handle hiring, sprint planning, code reviews, and delivery while you steer priorities.',
    bestFor: [
      'Building new products or features',
      'Scaling an existing engineering org',
      'Long-term roadmap execution',
      'Teams needing minimal oversight',
    ],
    notFor: [
      'One-off bug fixes or micro-tasks',
      'Projects under 3 months',
      'Clients who want to micromanage',
    ],
    timeline: '2–4 weeks to assemble',
    pricing: 'Monthly retainer by team size',
    control: 'High — you set priorities',
    management: 'We manage sprints & delivery',
    scalability: 'Add pods or specializations',
    commitment: 'Quarterly with renewal options',
    ip: 'Owned by you, secured by NDAs',
    pros: [
      { icon: 'ri-shield-check-line', text: 'Zero management overhead — we run the team' },
      { icon: 'ri-stack-line', text: 'Full squad with tech lead, engineers, QA, DevOps' },
      { icon: 'ri-arrow-up-circle-line', text: 'Easily scale by adding pods or skills over time' },
    ],
    cons: [
      { icon: 'ri-calendar-line', text: 'Takes 2–4 weeks to build the right team' },
      { icon: 'ri-hand-coin-line', text: 'Higher minimum commitment than single hires' },
    ],
  },
  {
    id: 'project',
    name: 'Project-Based',
    subtitle: 'Fixed-scope delivery',
    icon: 'ri-file-list-3-line',
    description:
      'A complete end-to-end build with a fixed scope, timeline, and budget. We scope, design, develop, test, and deploy the entire product or feature set. You get a finished deliverable and full IP transfer at the end.',
    bestFor: [
      'MVP builds from scratch',
      'Major feature launches',
      'Legacy system rewrites',
      'Defined scope with clear requirements',
    ],
    notFor: [
      'Open-ended R&D exploration',
      'Requirements that shift weekly',
      'Ongoing maintenance & iteration',
    ],
    timeline: 'Scope-first, then 4–16 weeks',
    pricing: 'Fixed-price milestone payments',
    control: 'Medium — you approve milestones',
    management: 'We run everything end-to-end',
    scalability: 'Scope changes = change orders',
    commitment: 'Fixed contract with milestones',
    ip: 'Full transfer on final delivery',
    pros: [
      { icon: 'ri-lock-line', text: 'Fixed budget — no surprise costs or overruns' },
      { icon: 'ri-checkbox-multiple-line', text: 'Clear milestones with demo checkpoints' },
      { icon: 'ri-award-line', text: 'Full IP ownership transferred at completion' },
    ],
    cons: [
      { icon: 'ri-edit-line', text: 'Scope changes require formal change orders' },
      { icon: 'ri-hourglass-line', text: 'Not suited for iterative agile pivots' },
    ],
  },
];

const comparisonFields = [
  { key: 'timeline', label: 'Time to Start' },
  { key: 'pricing', label: 'Pricing Model' },
  { key: 'control', label: 'Your Control' },
  { key: 'management', label: 'Management' },
  { key: 'scalability', label: 'Scalability' },
  { key: 'commitment', label: 'Commitment' },
  { key: 'ip', label: 'IP Ownership' },
];

export default function EngagementModels() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.05 });
  const [activeModel, setActiveModel] = useState('staff-aug');
  const [viewMode, setViewMode] = useState<'detail' | 'compare'>('detail');

  const activeData = models.find((m) => m.id === activeModel)!;

  return (
    <section id="engagement-models" className="py-1 md:py-1 bg-stone-50/60 relative overflow-hidden" ref={sectionRef}>
      {/* Subtle dot pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, #1c1917 1px, transparent 0)',
        backgroundSize: '24px 24px',
      }}></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-arrow-left-right-line text-primary-600"></i>
            <span className="text-sm font-medium text-gray-700">Engagement Models</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Choose Your
            <br />
            <span className="text-primary-600">Engagement Model</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            We offer three engagement models across our software development and talent services — whether you want us to build for you, place talent on your team, or deliver a fixed-scope project.
          </motion.p>
        </motion.div>

        {/* Model Selector Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.35, ...springTransition }}
          className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-4 mb-10 md:mb-14"
        >
          {models.map((model) => {
            const isActive = activeModel === model.id;
            return (
              <button
                key={model.id}
                onClick={() => setActiveModel(model.id)}
                className={`flex items-center gap-3 px-5 py-3 md:px-6 md:py-4 rounded-xl text-sm md:text-base font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap border ${
                  isActive
                    ? 'bg-navy-900 text-white border-navy-900 shadow-xl shadow-navy-900/15 scale-[1.02]'
                    : 'bg-white text-navy-900 border-gray-200 hover:border-primary-300 hover:bg-primary-50'
                }`}
              >
                <i className={`${model.icon} text-lg ${isActive ? 'text-primary-400' : 'text-primary-600'}`}></i>
                <span className="text-left">
                  <span className="block leading-tight">{model.name}</span>
                  <span className={`block text-[11px] md:text-xs font-normal mt-0.5 ${isActive ? 'text-gray-300' : 'text-gray-500'}`}>
                    {model.subtitle}
                  </span>
                </span>
              </button>
            );
          })}
        </motion.div>

        {/* View Toggle */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.45 }}
          className="flex justify-center mb-8 md:mb-10"
        >
          <div className="inline-flex bg-gray-100 rounded-full p-1">
            <button
              onClick={() => setViewMode('detail')}
              className={`px-4 py-2 text-xs md:text-sm font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap ${
                viewMode === 'detail'
                  ? 'bg-white text-navy-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Detailed View
            </button>
            <button
              onClick={() => setViewMode('compare')}
              className={`px-4 py-2 text-xs md:text-sm font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap ${
                viewMode === 'compare'
                  ? 'bg-white text-navy-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Side-by-Side Compare
            </button>
          </div>
        </motion.div>

        {/* Content */}
        <AnimatePresence mode="wait">
          {viewMode === 'detail' ? (
            <motion.div
              key={activeModel + '-detail'}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
                {/* Main Description Card */}
                <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-6 md:p-8 shadow-sm hover:border-primary-200 transition-colors">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-primary-50">
                      <i className={`${activeData.icon} text-xl md:text-2xl text-primary-600`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl md:text-2xl font-bold text-navy-900">{activeData.name}</h3>
                      <p className="text-sm text-gray-500">{activeData.subtitle}</p>
                    </div>
                  </div>

                  <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-6">{activeData.description}</p>

                  {/* Quick stats row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
                    {[
                      { label: 'Time to Start', value: activeData.timeline, icon: 'ri-time-line' },
                      { label: 'Pricing', value: activeData.pricing, icon: 'ri-exchange-dollar-line' },
                      { label: 'Your Control', value: activeData.control, icon: 'ri-equalizer-line' },
                      { label: 'Commitment', value: activeData.commitment, icon: 'ri-calendar-check-line' },
                    ].map((stat, i) => (
                      <div key={i} className="bg-gray-50 rounded-xl p-3 md:p-4">
                        <div className="flex items-center gap-1.5 mb-1">
                          <i className={`${stat.icon} text-xs text-primary-500`}></i>
                          <span className="text-[10px] md:text-xs font-semibold text-gray-500 uppercase tracking-wider">{stat.label}</span>
                        </div>
                        <p className="text-xs md:text-sm font-bold text-navy-900">{stat.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Best For / Not For */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                    <div>
                      <p className="text-sm font-bold text-navy-900 mb-3 flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-emerald-500"></i>
                        Best For
                      </p>
                      <ul className="space-y-2">
                        {activeData.bestFor.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                            <i className="ri-check-line text-emerald-500 mt-0.5 flex-shrink-0"></i>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-navy-900 mb-3 flex items-center gap-2">
                        <i className="ri-close-circle-line text-red-400"></i>
                        Not Ideal For
                      </p>
                      <ul className="space-y-2">
                        {activeData.notFor.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                            <i className="ri-close-line text-red-300 mt-0.5 flex-shrink-0"></i>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Pros & Cons */}
                <div className="space-y-5 md:space-y-6">
                  <div className="bg-emerald-50/50 rounded-2xl border border-emerald-100 p-5 md:p-6">
                    <h4 className="text-sm font-bold text-navy-900 mb-4 flex items-center gap-2">
                      <i className="ri-thumb-up-line text-emerald-600"></i>
                      Pros
                    </h4>
                    <ul className="space-y-3">
                      {activeData.pros.map((pro, i) => (
                        <li key={i} className="flex items-start gap-2.5">
                          <div className="w-6 h-6 flex items-center justify-center rounded-md bg-emerald-100 flex-shrink-0 mt-0.5">
                            <i className={`${pro.icon} text-xs text-emerald-600`}></i>
                          </div>
                          <span className="text-sm text-gray-700 leading-snug">{pro.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-red-50/50 rounded-2xl border border-red-100 p-5 md:p-6">
                    <h4 className="text-sm font-bold text-navy-900 mb-4 flex items-center gap-2">
                      <i className="ri-error-warning-line text-red-400"></i>
                      Trade-Offs
                    </h4>
                    <ul className="space-y-3">
                      {activeData.cons.map((con, i) => (
                        <li key={i} className="flex items-start gap-2.5">
                          <div className="w-6 h-6 flex items-center justify-center rounded-md bg-red-100 flex-shrink-0 mt-0.5">
                            <i className={`${con.icon} text-xs text-red-500`}></i>
                          </div>
                          <span className="text-sm text-gray-700 leading-snug">{con.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="compare"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="overflow-x-auto"
            >
              <div className="min-w-[700px]">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="text-left p-4 md:p-5 text-sm font-bold text-navy-900 bg-gray-50 rounded-tl-2xl w-1/4"></th>
                      {models.map((model, i) => (
                        <th
                          key={model.id}
                          className={`p-4 md:p-5 text-center w-1/4 ${
                            i === 0 ? 'bg-primary-50/50' : i === 1 ? 'bg-navy-900/5' : 'bg-accent-500/5'
                          } ${i === models.length - 1 ? 'rounded-tr-2xl' : ''}`}
                        >
                          <div className="flex flex-col items-center gap-2">
                            <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${
                              i === 0 ? 'bg-primary-100' : i === 1 ? 'bg-navy-800/10' : 'bg-accent-500/10'
                            }`}>
                              <i className={`${model.icon} text-lg ${
                                i === 0 ? 'text-primary-600' : i === 1 ? 'text-navy-900' : 'text-accent-600'
                              }`}></i>
                            </div>
                            <span className="text-sm font-bold text-navy-900">{model.name}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonFields.map((field, rowIdx) => (
                      <tr key={field.key} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                        <td className="p-4 md:p-5 text-sm font-semibold text-navy-900">{field.label}</td>
                        {models.map((model) => (
                          <td
                            key={model.id}
                            className="p-4 md:p-5 text-center text-sm text-gray-600"
                          >
                            {model[field.key as keyof EngagementModel] as string}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {/* Best For row */}
                    <tr className="bg-white">
                      <td className="p-4 md:p-5 text-sm font-semibold text-navy-900">Best For</td>
                      {models.map((model) => (
                        <td key={model.id} className="p-4 md:p-5">
                          <ul className="space-y-1">
                            {model.bestFor.slice(0, 2).map((item, i) => (
                              <li key={i} className="flex items-start gap-1.5 text-xs text-gray-600">
                                <i className="ri-check-line text-emerald-500 mt-0.5 flex-shrink-0"></i>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </td>
                      ))}
                    </tr>
                    {/* Commitment row */}
                    <tr className="bg-gray-50/50">
                      <td className="p-4 md:p-5 text-sm font-semibold text-navy-900 rounded-bl-2xl">Minimum Commitment</td>
                      {models.map((model, i) => (
                        <td
                          key={model.id}
                          className={`p-4 md:p-5 text-center text-sm font-semibold ${
                            i === models.length - 1 ? 'rounded-br-2xl' : ''
                          }`}
                        >
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200">
                            {model.commitment}
                          </span>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, ...springTransition }}
          className="mt-10 md:mt-14 text-center"
        >
          <div className="bg-primary-50 rounded-2xl border border-primary-100 p-6 md:p-8 max-w-2xl mx-auto">
            <p className="text-sm md:text-base text-gray-600 mb-4">
              Not sure which model fits your situation? Every client is different — tell us about your project and we will recommend the best approach.
            </p>
            <button
              onClick={() => {
                const el = document.getElementById('cta');
                if (el) {
                  const offset = 80;
                  const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
                  window.scrollTo({ top: pos, behavior: 'smooth' });
                }
              }}
              className="px-6 py-3 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
            >
              Get a Free Recommendation
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}